import { FiCheck, FiX } from 'react-icons/fi'

import { Alert } from '@vtex/shoreline'
import { t } from 'i18next'

import { Button } from '~/components/Buttons'
import { ModalConfirmations } from '~/components/CampaignReviewModal'
import Panel from '~/components/Panel'

import { useOffsiteReviewDecision } from './useOffsiteReviewDecision'

import * as S from './styles'

type Props = {
  campaignId?: string
  status?: CampaignStatus
  /** Motivo da rejeição, de `status_history[0].note` */
  message?: string
  /**
   * Indicador de permissão vindo do domínio. **`undefined` é permitido**, não
   * negado: o campo já existe em homologação, mas onde a resposta ainda não o
   * traz, esconder a ação a esconderia de qualquer revisor. A autorização real
   * é a escrita.
   *
   * O domínio manda só a conclusão, de propósito — nem o criador nem um motivo
   * enumerado são expostos. Por isso o aviso não afirma o porquê: hoje a regra
   * é segregação de funções, e ela pode mudar sem a interface saber.
   */
  canApprove?: boolean
  /** Para onde a decisão leva, e de onde o revisor veio. */
  queuePath: string
}

/**
 * Card de decisão da revisão offsite do anunciante.
 *
 * É o único lugar desta tela que conhece o estado da revisão e a permissão de
 * aprovar; o resto da página é leitura pura. Deliberadamente **não**
 * reutiliza `ReviewBar` nem `StatusBar`: os dois travam em `isPublisher()`, que
 * é o oposto do ator daqui.
 *
 * A decisão é **uma** chamada: o backend encadeia o despacho ao veiculador, e a
 * resposta já vem no estado posterior. Ver
 * `005-offsite-campaign-approval/contracts/approval-api.md`.
 */
export const OffsiteReviewActions = ({
  campaignId,
  status,
  message,
  canApprove,
  queuePath
}: Props) => {
  const {
    Modal,
    contentModal,
    showConfirmationModal,
    toggleConfirmationModal,
    handleSubmit,
    requestApproval,
    requestRejection,
    isPending,
    domainBlockMessage
  } = useOffsiteReviewDecision({ campaignId, queuePath })

  // Ausência é permissão. Ver o comentário da prop. Um 403 do domínio, ao
  // contrário, é a regra falando, e aí a ação sai.
  const mayApprove = (canApprove ?? true) && !domainBlockMessage
  const isAwaitingReview = status === 'pending_review'
  const isRejected = status === 'rejected'

  /**
   * Só estes dois estados a cópia de "enviada ao canal" descreve de fato. A fila
   * lista o ciclo de vida inteiro, então `paused`, `canceled` e `finished`
   * também chegam aqui — e dizer a eles que a campanha aguarda veiculação seria
   * simplesmente falso. Para esses, o estado real já está no topo da página.
   */
  const isDispatched =
    status === 'approved' || status === 'awaiting_publication'

  return (
    /* Sem contorno amarelo: o cartão de revisão do desenho tem moldura neutra,
       e o realce era invenção nossa — o estado já está na badge do topo. */
    <Panel
      template="shoreline"
      title={t('rm:offsiteAdvertiserReview.detail.actionsTitle')}
    >
      {isAwaitingReview && (
        <>
          {mayApprove ? (
            <S.Lead>
              {t('rm:offsiteAdvertiserReview.detail.awaitingLead')}
            </S.Lead>
          ) : (
            <Alert variant="warning">
              <p className="mb-0">
                <strong>
                  {t('rm:offsiteAdvertiserReview.detail.approvalBlocked.title')}
                </strong>{' '}
                {domainBlockMessage ||
                  t(
                    'rm:offsiteAdvertiserReview.detail.approvalBlocked.description'
                  )}
              </p>
            </Alert>
          )}

          <S.Actions>
            {/* Rejeitar continua disponível para quem não pode aprovar: o
                indicador do domínio fala de aprovação, e recusar não é o
                conflito que a regra existe para evitar. */}
            <Button
              template="critical"
              loading={isPending}
              disabled={isPending}
              onClick={requestRejection}
              text={t('rm:offsiteAdvertiserReview.detail.reject')}
              iconLeft={<FiX />}
            />

            {mayApprove && (
              <Button
                template="primary"
                loading={isPending}
                disabled={isPending}
                onClick={requestApproval}
                text={t('rm:offsiteAdvertiserReview.detail.approve')}
                iconLeft={<FiCheck />}
              />
            )}
          </S.Actions>
        </>
      )}

      {isRejected && (
        <>
          <S.Lead>{t('rm:offsiteAdvertiserReview.detail.rejectedLead')}</S.Lead>

          {!!message && (
            <S.Reason>
              <strong>
                {t('rm:offsiteAdvertiserReview.detail.rejectedReason')}:{' '}
              </strong>
              {message}
            </S.Reason>
          )}
        </>
      )}

      {/* Fallback deliberado: a fila lista o ciclo de vida inteiro, então
          qualquer status que ela mostre precisa renderizar aqui. */}
      {!isAwaitingReview && !isRejected && (
        <S.Lead>
          {isDispatched
            ? t('rm:offsiteAdvertiserReview.detail.sentLead')
            : t('rm:offsiteAdvertiserReview.detail.decidedLead')}
        </S.Lead>
      )}

      {!!contentModal && (
        <ModalConfirmations
          Modal={Modal}
          showConfirmationModal={showConfirmationModal}
          toggleConfirmationModal={toggleConfirmationModal}
          handleSubmit={handleSubmit}
          contentModal={contentModal}
          isSubmitting={isPending}
        />
      )}
    </Panel>
  )
}
